// Editor visual de fluxo — Fase 10.
//
// Quatro colunas de responsabilidade, e nenhuma delas mistura com a outra:
// a paleta cria passos, o canvas posiciona e liga, o painel configura, e a
// barra de baixo diz o que impede publicar.
//
// O QUE ESTA TELA GARANTE, e é o critério de aceite da fase: um fluxo com erro
// NÃO PUBLICA. O botão fica desligado e a lista de problemas aponta o passo
// culpado pelo nome. Publicar um fluxo quebrado seria descobrir o erro na
// conversa de um cliente, uma conversa por vez.
//
// O rascunho vive no navegador enquanto se edita e só vai ao banco no "Salvar".
// Salvar a cada tecla pareceria mais moderno e seria pior: cada gravação
// reescreve nós e arestas da versão inteira (ver `salvarGrafo`), e uma conexão
// instável no meio disso deixaria o rascunho pela metade.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, GitBranch, Loader2, Play, Plus,
  Save, ShieldAlert, Upload, Zap,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useInboxMembro } from '../../../src/hooks/useInboxMembro';
import { CATALOGO } from './catalogoNos';
import type { ArestaFluxo, Fluxo, Grafo, NoFluxo, TipoNo, Versao } from './tipos';
import {
  carregarFluxo, carregarGrafo, carregarMembros, carregarTags, carregarVersoes,
  novoRascunho, publicarVersao, salvarGrafo,
} from './apiFluxos';
import { erros, validarFluxo } from './validarFluxo';
import type { Ligando } from './componentes/Canvas';
import Canvas from './componentes/Canvas';
import PainelPropriedades from './componentes/PainelPropriedades';
import PainelSimulacao from './componentes/PainelSimulacao';
import PainelGatilhos from './componentes/PainelGatilhos';

/** Nome novo que não colide com os que já existem: enviar, enviar_2, … */
function chaveLivre(tipo: TipoNo, existentes: Set<string>): string {
  const base = tipo === 'enviar_mensagem' ? 'enviar'
    : tipo === 'aguardar_resposta' ? 'aguardar'
    : tipo === 'condicao' ? 'decidir'
    : tipo === 'atribuir_responsavel' ? 'atribuir'
    : tipo === 'aplicar_tag' ? 'etiqueta'
    : tipo === 'abrir_chamado' ? 'chamado'
    : tipo === 'chamar_webhook' ? 'webhook'
    : tipo;
  if (!existentes.has(base)) return base;
  let n = 2;
  while (existentes.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export default function EditorFluxoView() {
  const { flowId = '' } = useParams();
  const { profile } = useAuth();
  const { ehAdmin, carregando: carregandoAcesso } = useInboxMembro(profile?.id);

  const [fluxo, setFluxo] = useState<Fluxo | null>(null);
  const [versoes, setVersoes] = useState<Versao[]>([]);
  const [versaoAtiva, setVersaoAtiva] = useState<Versao | null>(null);
  const [grafo, setGrafo] = useState<Grafo>({ nos: [], arestas: [], noInicial: null });

  const [tags, setTags] = useState<{ id: string; nome: string; cor: string }[]>([]);
  const [membros, setMembros] = useState<{ id: string; nome: string }[]>([]);

  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [ligando, setLigando] = useState<Ligando | null>(null);
  const [sujo, setSujo] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // Um painel de cada vez na coluna da direita. Empilhar os três deixaria a
  // coluna alta demais para a tela de notebook, que é o alvo desta fase.
  const [painel, setPainel] = useState<'propriedades' | 'simulacao' | 'gatilhos'>('propriedades');
  const [caminhoSimulado, setCaminhoSimulado] = useState<string[]>([]);

  const somenteLeitura = versaoAtiva?.status !== 'rascunho';

  // ── Carga ─────────────────────────────────────────────────────────────────
  const recarregar = useCallback(async () => {
    if (!flowId) return;
    setCarregando(true);
    setErro(null);
    try {
      const [f, vs] = await Promise.all([carregarFluxo(flowId), carregarVersoes(flowId)]);
      if (!f) throw new Error('Fluxo não encontrado.');
      setFluxo(f);
      setVersoes(vs);

      // Abre no rascunho, se houver; senão na publicada; senão na mais recente.
      // Cair sempre na mais recente abriria uma arquivada logo depois de
      // publicar, e pareceria que a publicação não pegou.
      const escolhida = vs.find((v) => v.status === 'rascunho')
        ?? vs.find((v) => v.status === 'publicada')
        ?? vs[0]
        ?? null;
      setVersaoAtiva(escolhida);
      setGrafo(escolhida ? await carregarGrafo(escolhida.id) : { nos: [], arestas: [], noInicial: null });
      setSujo(false);

      const [t, m] = await Promise.all([carregarTags(f.org_id), carregarMembros(f.org_id)]);
      setTags(t);
      setMembros(m);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [flowId]);

  useEffect(() => { void recarregar(); }, [recarregar]);

  // Sair com rascunho não salvo perde o trabalho — e o canvas não parece um
  // formulário, então ninguém espera perder.
  useEffect(() => {
    if (!sujo) return;
    const aoSair = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', aoSair);
    return () => window.removeEventListener('beforeunload', aoSair);
  }, [sujo]);

  const problemas = useMemo(() => validarFluxo(grafo), [grafo]);
  const impedimentos = useMemo(() => erros(problemas), [problemas]);
  const noSelecionado = useMemo(
    () => grafo.nos.find((n) => n.node_key === selecionado) ?? null,
    [grafo.nos, selecionado],
  );

  // ── Edição do grafo ───────────────────────────────────────────────────────
  const mexer = useCallback((mudanca: (g: Grafo) => Grafo) => {
    setGrafo((atual) => mudanca(atual));
    setSujo(true);
  }, []);

  const adicionarPasso = useCallback((tipo: TipoNo) => {
    if (somenteLeitura) return;
    mexer((g) => {
      const chave = chaveLivre(tipo, new Set(g.nos.map((n) => n.node_key)));
      // Empilha na diagonal para o passo novo não nascer exatamente em cima do
      // anterior — dois nós sobrepostos parecem um só, e o de baixo some.
      const novo: NoFluxo = {
        id: crypto.randomUUID(),
        version_id: versaoAtiva?.id ?? '',
        node_key: chave,
        tipo,
        config: {},
        posicao: { x: 120 + (g.nos.length % 4) * 60, y: 80 + g.nos.length * 46 },
      };
      setSelecionado(chave);
      return {
        ...g,
        nos: [...g.nos, novo],
        // O primeiro passo criado vira o início: é o que a pessoa espera, e
        // esquecer de marcar o início é o erro de publicação mais comum.
        noInicial: g.noInicial ?? chave,
      };
    });
  }, [mexer, somenteLeitura, versaoAtiva?.id]);

  const moverPasso = useCallback((noKey: string, posicao: { x: number; y: number }) => {
    mexer((g) => ({
      ...g,
      nos: g.nos.map((n) => (n.node_key === noKey ? { ...n, posicao } : n)),
    }));
  }, [mexer]);

  /**
   * Renomear reaponta as ligações junto.
   *
   * As arestas guardam o NOME do passo, não o id da linha. Renomear sem
   * reapontar deixaria arestas órfãs — o validador acusaria "liga para um passo
   * que não existe" e a causa (o rename) já teria saído da tela.
   */
  const renomearPasso = useCallback((antigo: string, nova: string) => {
    const limpa = nova.trim().replace(/\s+/g, '_');
    if (!limpa) return;
    mexer((g) => {
      if (g.nos.some((n) => n.node_key === limpa && n.node_key !== antigo)) return g;
      return {
        nos: g.nos.map((n) => (n.node_key === antigo ? { ...n, node_key: limpa } : n)),
        arestas: g.arestas.map((a) => ({
          ...a,
          de: a.de === antigo ? limpa : a.de,
          para: a.para === antigo ? limpa : a.para,
        })),
        noInicial: g.noInicial === antigo ? limpa : g.noInicial,
      };
    });
    setSelecionado(limpa);
  }, [mexer]);

  const apagarPasso = useCallback((noKey: string) => {
    mexer((g) => ({
      nos: g.nos.filter((n) => n.node_key !== noKey),
      // As ligações do passo apagado vão junto: deixá-las viraria aresta
      // apontando para o vazio, com erro que fala de ligação e não de exclusão.
      arestas: g.arestas.filter((a) => a.de !== noKey && a.para !== noKey),
      noInicial: g.noInicial === noKey ? null : g.noInicial,
    }));
    setSelecionado(null);
  }, [mexer]);

  const ligar = useCallback((para: string) => {
    if (!ligando) return;
    mexer((g) => {
      const jaExiste = g.arestas.some(
        (a) => a.de === ligando.de && a.para === para && a.quando === ligando.quando,
      );
      if (jaExiste) return g;
      const nova: ArestaFluxo = {
        id: crypto.randomUUID(),
        version_id: versaoAtiva?.id ?? '',
        de: ligando.de,
        para,
        quando: ligando.quando,
      };
      return { ...g, arestas: [...g.arestas, nova] };
    });
    setLigando(null);
  }, [ligando, mexer, versaoAtiva?.id]);

  const removerAresta = useCallback((arestaId: string) => {
    mexer((g) => ({ ...g, arestas: g.arestas.filter((a) => a.id !== arestaId) }));
  }, [mexer]);

  const definirInicial = useCallback((noKey: string) => {
    mexer((g) => ({ ...g, noInicial: noKey }));
  }, [mexer]);

  // ── Salvar e publicar ─────────────────────────────────────────────────────
  const salvar = useCallback(async () => {
    if (!versaoAtiva) return;
    setSalvando(true);
    setErro(null);
    try {
      await salvarGrafo(versaoAtiva.id, grafo);
      setSujo(false);
      setAviso('Rascunho salvo.');
      setTimeout(() => setAviso(null), 2500);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }, [grafo, versaoAtiva]);

  const publicar = useCallback(async () => {
    if (!versaoAtiva) return;
    if (impedimentos.length > 0) return;
    if (!confirm(
      'Publicar esta versão? Ela passa a valer para as conversas novas. '
      + 'Quem já está no meio de um fluxo continua na versão antiga.',
    )) return;

    setPublicando(true);
    setErro(null);
    try {
      // Publicar o que está na tela, não o que está no banco: sem salvar antes,
      // a versão publicada seria a de antes das últimas edições — e a tela
      // mostraria o fluxo novo dizendo que ele está no ar.
      if (sujo) await salvarGrafo(versaoAtiva.id, grafo);
      await publicarVersao(versaoAtiva.id);
      setSujo(false);
      await recarregar();
      setAviso('Versão publicada.');
      setTimeout(() => setAviso(null), 3000);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setPublicando(false);
    }
  }, [grafo, impedimentos.length, recarregar, sujo, versaoAtiva]);

  const criarRascunho = useCallback(async () => {
    if (!fluxo) return;
    setSalvando(true);
    setErro(null);
    try {
      const base = versoes.find((v) => v.status === 'publicada') ?? versaoAtiva;
      const nova = await novoRascunho(fluxo.id, base);
      await recarregar();
      setVersaoAtiva(nova);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }, [fluxo, recarregar, versaoAtiva, versoes]);

  const trocarVersao = useCallback(async (versionId: string) => {
    if (sujo && !confirm('Você tem alterações não salvas neste rascunho. Trocar de versão descarta o que não foi salvo.')) return;
    const v = versoes.find((x) => x.id === versionId) ?? null;
    if (!v) return;
    setCarregando(true);
    try {
      setVersaoAtiva(v);
      setGrafo(await carregarGrafo(v.id));
      setSelecionado(null);
      setSujo(false);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [sujo, versoes]);

  // ── Bordas ────────────────────────────────────────────────────────────────
  if (carregandoAcesso || carregando) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-gray-300 dark:text-slate-600" />
      </div>
    );
  }

  if (!ehAdmin) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
        <ShieldAlert className="mx-auto mb-3 text-gray-400" size={28} />
        <p className="font-semibold text-gray-800 dark:text-slate-100">Acesso restrito</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Montar fluxo é decidir o que o sistema escreve sozinho para o cliente. Só administradores
          do Omnichannel entram aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3 px-4 md:px-6">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/inbox/fluxos"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700"
          >
            <ArrowLeft size={17} />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold leading-tight text-gray-900 dark:text-white">
              {fluxo?.nome ?? 'Fluxo'}
            </h1>
            <p className="flex items-center gap-1.5 text-xs leading-tight text-gray-500 dark:text-slate-400">
              <GitBranch size={11} />
              versão {versaoAtiva?.numero ?? '—'}
              <span className={
                versaoAtiva?.status === 'publicada' ? 'font-semibold text-primary'
                  : versaoAtiva?.status === 'rascunho' ? 'font-semibold text-secondary'
                  : ''
              }>
                · {versaoAtiva?.status ?? 'sem versão'}
              </span>
              {sujo && <span className="font-semibold text-secondary">· não salvo</span>}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {versoes.length > 1 && (
            <select
              value={versaoAtiva?.id ?? ''}
              onChange={(e) => void trocarVersao(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
            >
              {versoes.map((v) => (
                <option key={v.id} value={v.id}>v{v.numero} · {v.status}</option>
              ))}
            </select>
          )}

          <button
            onClick={() => setPainel((p) => (p === 'gatilhos' ? 'propriedades' : 'gatilhos'))}
            className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${
              painel === 'gatilhos'
                ? 'border-secondary bg-secondary/10 text-[#8A5D0F] dark:text-secondary'
                : 'border-gray-200 text-gray-600 hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            <Zap size={13} />
            Gatilhos
          </button>

          <button
            onClick={() => setPainel((p) => (p === 'simulacao' ? 'propriedades' : 'simulacao'))}
            className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${
              painel === 'simulacao'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-gray-200 text-gray-600 hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            <Play size={13} />
            Simular
          </button>

          {somenteLeitura ? (
            <button
              onClick={() => void criarRascunho()}
              disabled={salvando}
              className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <Plus size={13} />
              Editar em uma nova versão
            </button>
          ) : (
            <>
              <button
                onClick={() => void salvar()}
                disabled={salvando || !sujo}
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-40 dark:border-slate-700 dark:text-slate-300"
              >
                {salvando ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Salvar
              </button>
              <button
                onClick={() => void publicar()}
                disabled={publicando || impedimentos.length > 0}
                title={impedimentos.length > 0 ? 'Corrija os erros antes de publicar' : undefined}
                className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {publicando ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Publicar
              </button>
            </>
          )}
        </div>
      </header>

      {erro && (
        <div className="shrink-0 rounded-xl bg-red-50 px-3.5 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {erro}
        </div>
      )}
      {aviso && (
        <div className="flex shrink-0 items-center gap-1.5 rounded-xl bg-emerald-50 px-3.5 py-2 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
          <CheckCircle2 size={14} />
          {aviso}
        </div>
      )}
      {somenteLeitura && (
        <div className="shrink-0 rounded-xl bg-gray-100 px-3.5 py-2 text-xs text-gray-600 dark:bg-slate-700/50 dark:text-slate-300">
          Esta versão está <strong>{versaoAtiva?.status}</strong> e não pode ser editada — é o histórico
          do que rodou. Para mudar o fluxo, crie uma nova versão a partir dela.
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[170px_minmax(0,1fr)_290px]">
        {/* Paleta */}
        <div className="hidden overflow-y-auto rounded-2xl bg-white p-2 ring-1 ring-gray-200 lg:block dark:bg-slate-800 dark:ring-slate-700">
          <p className="px-1.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
            Passos
          </p>
          <div className="space-y-1">
            {CATALOGO.map((t) => (
              <button
                key={t.tipo}
                disabled={somenteLeitura}
                onClick={() => adicionarPasso(t.tipo)}
                title={t.resumo}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${t.cor}`} />
                <span className="truncate">{t.rotulo}</span>
              </button>
            ))}
          </div>
          {/* O passo que fala com o cliente é o único com consequência fora do
              sistema. Vale dizer isso onde ele é escolhido. */}
          <p className="mt-3 border-t border-gray-100 px-1.5 pt-2 text-[10px] leading-snug text-gray-400 dark:border-slate-700 dark:text-slate-500">
            "Enviar mensagem" escreve para o cliente de verdade. O limite é 5 por conversa.
          </p>
        </div>

        <div className="relative min-h-0">
          <Canvas
            grafo={grafo}
            selecionado={selecionado}
            problemas={problemas}
            ligando={ligando}
            destacados={caminhoSimulado}
            somenteLeitura={somenteLeitura}
            onSelecionar={setSelecionado}
            onMover={moverPasso}
            onIniciarLigacao={setLigando}
            onLigar={ligar}
            onRemoverAresta={removerAresta}
            onDefinirInicial={definirInicial}
          />

          {/* Barra de problemas: fica sobre o canvas, no rodapé, porque é o que
              a pessoa precisa ver no instante em que tenta publicar. */}
          {problemas.length > 0 && (
            <div className="pointer-events-auto absolute inset-x-2 bottom-2 max-h-32 overflow-y-auto rounded-xl bg-white/95 p-2 shadow-lg ring-1 ring-gray-200 backdrop-blur dark:bg-slate-800/95 dark:ring-slate-700">
              {problemas.map((p, i) => (
                <button
                  key={i}
                  onClick={() => p.noKey && setSelecionado(p.noKey)}
                  className="flex w-full items-start gap-1.5 rounded-lg px-1.5 py-1 text-left text-[11px] leading-snug transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/50"
                >
                  <AlertTriangle
                    size={12}
                    className={`mt-0.5 shrink-0 ${p.nivel === 'erro' ? 'text-red-500' : 'text-secondary'}`}
                  />
                  <span className="text-gray-600 dark:text-slate-300">
                    {p.noKey && <strong className="text-gray-800 dark:text-slate-100">{p.noKey}: </strong>}
                    {p.mensagem}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {painel === 'simulacao' ? (
          <PainelSimulacao
            grafo={grafo}
            versaoId={versaoAtiva?.id ?? null}
            flowId={flowId}
            podeSimular={impedimentos.length === 0}
            onCaminho={setCaminhoSimulado}
            onFechar={() => { setPainel('propriedades'); setCaminhoSimulado([]); }}
          />
        ) : painel === 'gatilhos' ? (
          <PainelGatilhos
            flowId={flowId}
            orgId={fluxo?.org_id ?? null}
            onFechar={() => setPainel('propriedades')}
          />
        ) : (
          <PainelPropriedades
            no={noSelecionado}
            problemas={problemas}
            tags={tags}
            membros={membros}
            ehInicial={grafo.noInicial === selecionado}
            somenteLeitura={somenteLeitura}
            onMudarChave={(nova) => noSelecionado && renomearPasso(noSelecionado.node_key, nova)}
            onMudarConfig={(config) =>
              noSelecionado && mexer((g) => ({
                ...g,
                nos: g.nos.map((n) => (n.node_key === noSelecionado.node_key ? { ...n, config } : n)),
              }))
            }
            onDefinirInicial={() => noSelecionado && definirInicial(noSelecionado.node_key)}
            onApagar={() => noSelecionado && apagarPasso(noSelecionado.node_key)}
          />
        )}
      </div>
    </div>
  );
}
